import { useMemo, useState } from "react"
import { IconButton, Stack, Typography } from "@mui/material"
import { useFormikContext } from "formik"
import { makeStyles } from "tss-react/mui"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPlus, faTrashCan } from "@fortawesome/pro-light-svg-icons"

import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"

import { WorkgroupFormValues, WorkgroupMember } from "../workgroupsTypes"
import { AddWorkgroupMemberDialog } from "./AddWorkgroupMemberDialog"

const useStyles = makeStyles()((theme) => ({
    memberCard: {
        padding: ".25rem .5rem .25rem 1rem",
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,
        borderRadius: ".5rem",
    },
    memberName: {
        fontWeight: 600,
        color: theme.palette.tailwind.slate[900],
    },
    trashIcon: {
        color: theme.palette.tailwind.slate[600],
        fontSize: "1rem",
    },
    empty: {
        fontStyle: "italic",
        color: theme.palette.tailwind.slate[400],
        padding: ".5rem 0",
    },
    addButton: {
        "&.MuiButton-root": {
            color: theme.palette.primary.main,
            borderColor: theme.palette.primary.main,
            padding: "0 .75rem",
            borderRadius: ".5rem",
            "& .MuiButton-startIcon svg": {
                fontSize: ".75rem",
            },
        },
    },
}))

export function WorkgroupMembersPanel() {
    const { classes } = useStyles()
    const { values, setFieldValue } = useFormikContext<WorkgroupFormValues>()

    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)

    const currentMembers = values.members
    const memberIds = useMemo(() => currentMembers.map((member) => member.id), [currentMembers])

    const handleAdd = (member: WorkgroupMember) => {
        setFieldValue("members", [...currentMembers, member])
        setIsAddDialogOpen(false)
    }

    const handleRemove = (memberId: number) => {
        setFieldValue(
            "members",
            currentMembers.filter((member) => member.id !== memberId),
        )
    }

    return (
        <>
            {currentMembers.length > 0 ? (
                <Stack spacing={1}>
                    {currentMembers.map((member) => (
                        <Stack
                            key={member.id}
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            className={classes.memberCard}
                        >
                            <Typography variant="body2" className={classes.memberName}>
                                {member.displayName}
                            </Typography>
                            <LocalizedStrict
                                id="create-or-edit-company-workgroup-members-remove"
                                attrs={{ "aria-label": true }}
                            >
                                <IconButton aria-label="Remove" onClick={() => handleRemove(member.id)}>
                                    <FontAwesomeIcon icon={faTrashCan} className={classes.trashIcon} />
                                </IconButton>
                            </LocalizedStrict>
                        </Stack>
                    ))}
                </Stack>
            ) : (
                <LocalizedStrict id="create-or-edit-company-workgroup-members-empty">
                    <Typography variant="body2" className={classes.empty}>
                        This workgroup has no members yet.
                    </Typography>
                </LocalizedStrict>
            )}

            <Stack direction="row" justifyContent="flex-end">
                <LocalizedStrict id="create-or-edit-company-workgroup-members-add-button">
                    <StandardButton
                        variant="outlined"
                        fullWidth={false}
                        className={classes.addButton}
                        startIcon={<FontAwesomeIcon icon={faPlus} />}
                        onClick={() => setIsAddDialogOpen(true)}
                    >
                        Add
                    </StandardButton>
                </LocalizedStrict>
            </Stack>

            {isAddDialogOpen && (
                <AddWorkgroupMemberDialog
                    open={isAddDialogOpen}
                    excludedUserIds={memberIds}
                    onClose={() => setIsAddDialogOpen(false)}
                    onAdd={handleAdd}
                />
            )}
        </>
    )
}
